// ---------------------------------------------------------------------------
// Flow Bridge — converts between Flow JSON and visual canvas models.
//
// Flutter 1:1: story_actionflow_bridge.dart conversion logic
// ---------------------------------------------------------------------------

import type { Flow, FlowStep, FlowTrigger, BasicStep, ConditionalStep, LoopStep, SwitchStep, ParallelStep, TryCatchStep, StageStep, SagaStep } from '@cogo/types-v2'
import type { ActionFlowNode, ActionFlowConnection, ActionNodeType, ConnectionType } from '@/features/flow-editor/models/flow-models'
import { createNode, createConnection } from '@/features/flow-editor/models/flow-models'

// ---- Flow → Canvas ----

export function normalizeFlowTrigger(value: unknown): FlowTrigger {
  if (value === 'event' || value === 'init' || value === 'lifecycle' || value === 'manual') return value
  if (value === 'timer') return 'manual'
  return 'event'
}

export function flowToCanvas(
  flow: Flow,
  flowName: string,
): { nodes: ActionFlowNode[]; connections: ActionFlowConnection[] } {
  const nodes: ActionFlowNode[] = []
  const connections: ActionFlowConnection[] = []

  // Add trigger node
  const triggerNode = createNode('af_trigger', 'trigger', 'trigger', { x: 100, y: 200 }, flowName)
  triggerNode.parameters = { trigger: normalizeFlowTrigger(flow.trigger) }
  nodes.push(triggerNode)

  // Convert steps
  const stepNodes = stepsToNodes(flow.steps, 1)
  nodes.push(...stepNodes.nodes)
  connections.push(...stepNodes.connections)

  // Connect trigger to first step
  if (stepNodes.nodes.length > 0) {
    connections.push(createConnection('af_trigger', stepNodes.nodes[0].id, 'next'))
  }

  // Connect sequential steps
  for (let i = 0; i < stepNodes.linearIds.length - 1; i++) {
    const sourceId = stepNodes.linearIds[i]
    const targetId = stepNodes.linearIds[i + 1]
    // Only connect if not already connected (conditional/callback have their own connections)
    const sourceNode = stepNodes.nodes.find((n) => n.id === sourceId)
    if (
      sourceNode &&
      (
        sourceNode.nodeType === 'basic' ||
        (sourceNode.nodeType === 'group' && (sourceNode.actionType === 'stage' || sourceNode.actionType === 'saga'))
      )
    ) {
      connections.push(createConnection(sourceId, targetId, 'next'))
    }
  }

  return { nodes, connections }
}

interface StepConversion {
  nodes: ActionFlowNode[]
  connections: ActionFlowConnection[]
  linearIds: string[]
}

function stepsToNodes(steps: FlowStep[], startId: number): StepConversion {
  const nodes: ActionFlowNode[] = []
  const connections: ActionFlowConnection[] = []
  const linearIds: string[] = []
  let nextId = startId

  for (const step of steps) {
    const nodeId = `af_${nextId++}`
    const { node, extraNodes, extraConnections, idIncrement } = stepToNode(step, nodeId, nextId)

    node.position = calculatePosition(nodes.length, nodes)
    nodes.push(node)
    linearIds.push(nodeId)

    if (extraNodes) nodes.push(...extraNodes)
    if (extraConnections) connections.push(...extraConnections)
    nextId += idIncrement
  }

  return { nodes, connections, linearIds }
}

interface StepNodeResult {
  node: ActionFlowNode
  extraNodes?: ActionFlowNode[]
  extraConnections?: ActionFlowConnection[]
  idIncrement: number
}

function stepToNode(step: FlowStep, nodeId: string, nextAvailableId: number): StepNodeResult {
  const nodeType = mapStepToNodeType(step)

  // Coogo JSON steps may omit `type` — infer from `action` field
  const stepType = step.type ?? ('action' in step ? 'basic' : undefined)

  switch (stepType) {
    case 'basic':
    case 'expression': {
      const s = step as BasicStep
      return {
        node: createNode(nodeId, nodeType, s.action, { x: 0, y: 0 }, s.action),
        idIncrement: 0,
      }
    }

    case 'conditional': {
      const s = step as ConditionalStep
      const node = createNode(nodeId, 'conditional', 'conditional_action', { x: 0, y: 0 }, 'If/Else')
      node.parameters = { condition: s.condition }

      const extraNodes: ActionFlowNode[] = []
      const extraConnections: ActionFlowConnection[] = []
      let idInc = 0

      // Then branch
      if (s.then.length > 0) {
        const thenId = `af_${nextAvailableId + idInc}`
        const thenNode = createNode(thenId, 'basic', (s.then[0] as BasicStep).action ?? 'then', calculatePosition(1, [node]), 'Then')
        thenNode.parameters = (s.then[0] as BasicStep).params ?? {}
        extraNodes.push(thenNode)
        extraConnections.push(createConnection(nodeId, thenId, 'conditionTrue'))
        idInc++
      }

      // Else branch
      if (s.else && s.else.length > 0) {
        const elseId = `af_${nextAvailableId + idInc}`
        const elseNode = createNode(elseId, 'basic', (s.else[0] as BasicStep).action ?? 'else', calculatePosition(2, [node]), 'Else')
        elseNode.parameters = (s.else[0] as BasicStep).params ?? {}
        extraNodes.push(elseNode)
        extraConnections.push(createConnection(nodeId, elseId, 'conditionFalse'))
        idInc++
      }

      return { node, extraNodes, extraConnections, idIncrement: idInc }
    }

    case 'loop': {
      const s = step as LoopStep
      const node = createNode(nodeId, 'loop', 'loop_action', { x: 0, y: 0 }, 'Loop')
      node.parameters = { collection: s.collection, itemVar: s.itemVar }
      return { node, idIncrement: 0 }
    }

    case 'switch': {
      const s = step as SwitchStep
      const node = createNode(nodeId, 'switchNode', 'switch_action', { x: 0, y: 0 }, 'Switch')
      node.parameters = { value: s.value, cases: Object.keys(s.cases) }
      return { node, idIncrement: 0 }
    }

    case 'parallel': {
      const s = step as ParallelStep
      const node = createNode(nodeId, 'group', 'parallel', { x: 0, y: 0 }, 'Parallel')
      node.parameters = { stepCount: s.steps.length }
      return { node, idIncrement: 0 }
    }

    case 'tryCatch': {
      const s = step as TryCatchStep
      const node = createNode(nodeId, 'callback', 'try_catch', { x: 0, y: 0 }, 'Try/Catch')
      node.parameters = { trySteps: s.try.length, catchSteps: s.catch?.length ?? 0, finallySteps: s.finally?.length ?? 0 }
      return { node, idIncrement: 0 }
    }

    case 'stage': {
      const s = step as StageStep
      const node = createNode(nodeId, 'group', 'stage', { x: 0, y: 0 }, `Stage: ${s.name}`)
      node.parameters = {
        name: s.name,
        failurePolicy: s.failurePolicy ?? 'stop',
        stepCount: s.steps.length,
        compensationCount: s.compensate?.length ?? 0,
        __sourceStep: s,
      }
      return { node, idIncrement: 0 }
    }

    case 'saga': {
      const s = step as SagaStep
      const node = createNode(nodeId, 'group', 'saga', { x: 0, y: 0 }, 'Saga')
      node.parameters = {
        stageCount: s.stages.length,
        compensationFailurePolicy: s.compensationFailurePolicy ?? 'continue',
        __sourceStep: s,
      }
      return { node, idIncrement: 0 }
    }

    default:
      return {
        node: createNode(nodeId, 'basic', 'unknown', { x: 0, y: 0 }, 'Unknown'),
        idIncrement: 0,
      }
  }
}

// ---- Canvas → Flow ----

export function canvasToFlow(
  nodes: ActionFlowNode[],
  connections: ActionFlowConnection[],
  flowName: string,
  trigger: FlowTrigger = 'event',
): Flow {
  const triggerNode = nodes.find((n) => n.nodeType === 'trigger')
  const steps: FlowStep[] = []

  // Build adjacency: sourceId → [{ targetId, connectionType }]
  const adj = new Map<string, { targetId: string; connectionType: ConnectionType }[]>()
  for (const c of connections) {
    const list = adj.get(c.sourceNodeId) ?? []
    list.push({ targetId: c.targetNodeId, connectionType: c.connectionType })
    adj.set(c.sourceNodeId, list)
  }

  // BFS from trigger
  if (triggerNode) {
    const visited = new Set<string>()
    const queue = adj.get(triggerNode.id)?.map((e) => e.targetId) ?? []

    while (queue.length > 0) {
      const nodeId = queue.shift()!
      if (visited.has(nodeId)) continue
      visited.add(nodeId)

      const node = nodes.find((n) => n.id === nodeId)
      if (!node) continue

      const step = nodeToStep(node, connections, nodes)
      if (step) steps.push(step)

      // Follow 'next' connections
      const nexts = adj.get(nodeId)?.filter((e) => e.connectionType === 'next') ?? []
      for (const n of nexts) {
        if (!visited.has(n.targetId)) queue.push(n.targetId)
      }
    }
  }

  return {
    name: flowName,
    trigger: normalizeFlowTrigger(trigger),
    steps,
  }
}

function nodeToStep(
  node: ActionFlowNode,
  connections: ActionFlowConnection[],
  allNodes: ActionFlowNode[],
): FlowStep | null {
  switch (node.nodeType) {
    case 'basic':
      return {
        type: 'basic',
        action: node.actionType,
        params: node.parameters,
      } as BasicStep

    case 'conditional': {
      const trueConn = connections.find(
        (c) => c.sourceNodeId === node.id && c.connectionType === 'conditionTrue',
      )
      const falseConn = connections.find(
        (c) => c.sourceNodeId === node.id && c.connectionType === 'conditionFalse',
      )

      const thenSteps: FlowStep[] = []
      const elseSteps: FlowStep[] = []

      if (trueConn) {
        const trueNode = allNodes.find((n) => n.id === trueConn.targetNodeId)
        if (trueNode) {
          const step = nodeToStep(trueNode, connections, allNodes)
          if (step) thenSteps.push(step)
        }
      }
      if (falseConn) {
        const falseNode = allNodes.find((n) => n.id === falseConn.targetNodeId)
        if (falseNode) {
          const step = nodeToStep(falseNode, connections, allNodes)
          if (step) elseSteps.push(step)
        }
      }

      return {
        type: 'conditional',
        condition: (node.parameters['condition'] as string) ?? '',
        then: thenSteps,
        else: elseSteps.length > 0 ? elseSteps : undefined,
      } as ConditionalStep
    }

    case 'loop':
      return {
        type: 'loop',
        collection: (node.parameters['collection'] as string) ?? '',
        itemVar: (node.parameters['itemVar'] as string) ?? 'item',
        steps: [],
      } as LoopStep

    case 'switchNode': {
      const cases: Record<string, FlowStep[]> = {}
      const caseNames = (node.parameters['cases'] as string[]) ?? []
      for (const caseName of caseNames) {
        cases[caseName] = []
      }
      return {
        type: 'switch',
        value: (node.parameters['value'] as string) ?? '',
        cases,
      } as SwitchStep
    }

    case 'callback':
      return {
        type: 'tryCatch',
        try: [],
        catch: [],
      } as TryCatchStep

    case 'group':
      if (node.actionType === 'stage') {
        const source = node.parameters['__sourceStep'] as StageStep | undefined
        const requestedPolicy = node.parameters['failurePolicy'] === 'continue' ? 'continue' : 'stop'
        if (source?.type === 'stage') {
          return {
            ...source,
            name: (node.parameters['name'] as string) || source.name,
            failurePolicy: requestedPolicy === 'continue' && source.onError?.length ? 'continue' : 'stop',
          } as StageStep
        }
        return {
          type: 'stage',
          name: (node.parameters['name'] as string) || 'stage',
          steps: [],
          failurePolicy: 'stop',
        } as StageStep
      }
      if (node.actionType === 'saga') {
        const source = node.parameters['__sourceStep'] as SagaStep | undefined
        if (source?.type === 'saga') {
          return {
            ...source,
            compensationFailurePolicy: node.parameters['compensationFailurePolicy'] === 'stop' ? 'stop' : 'continue',
          }
        }
        return {
          type: 'saga',
          stages: [{ type: 'stage', name: 'stage1', steps: [] }],
          compensationFailurePolicy: node.parameters['compensationFailurePolicy'] === 'stop' ? 'stop' : 'continue',
        } as SagaStep
      }
      return {
        type: 'parallel',
        steps: [],
      } as ParallelStep

    case 'trigger':
    case 'stickyNote':
      return null
  }
}

// ---- Helpers ----

function mapStepToNodeType(step: FlowStep): ActionNodeType {
  // Coogo JSON steps may omit `type` — infer from presence of `action` field
  const t = step.type ?? ('action' in step ? 'basic' : undefined)
  switch (t) {
    case 'basic': return 'basic'
    case 'expression': return 'basic'
    case 'conditional': return 'conditional'
    case 'loop': return 'loop'
    case 'switch': return 'switchNode'
    case 'parallel': return 'group'
    case 'tryCatch': return 'callback'
    case 'stage': return 'group'
    case 'saga': return 'group'
    default: return 'basic'
  }
}

function calculatePosition(
  index: number,
  existingNodes: ActionFlowNode[],
): { x: number; y: number } {
  if (existingNodes.length === 0) {
    return { x: 100 + index * 200, y: 200 }
  }
  const lastNode = existingNodes[existingNodes.length - 1]
  return { x: lastNode.position.x + 200, y: lastNode.position.y }
}
